import { useState, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type SortingState } from "@tanstack/react-table";
import { type components as AdminComponents } from "@njbsoft/types/admin";
import { useAdminPermits, type AdminPermitsParams } from "@/hooks/use-admin-permits";
import { useDebounce } from "@/hooks/use-debounce";
import { DataTable } from "@/components/data-table";
import { Pagination } from "@/components/pagination";
import { usePermitColumns } from "./permit-columns";
import { PermitFilters } from "./permit-filters";

type AdminPermitListItem = AdminComponents["schemas"]["AdminPermitListItem"];

export const Route = createFileRoute("/_admin/permits/")({
  component: AdminPermitsListPage,
});

const PAGE_SIZE = 20;

function AdminPermitsListPage() {
  const navigate = useNavigate();
  const columns = usePermitColumns();

  // Filter state
  const [search, setSearch] = useState("");
  const [permitType, setPermitType] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: "created_at", desc: true }]);

  const debouncedSearch = useDebounce(search, 300);

  // Build query params
  const params: AdminPermitsParams = useMemo(() => {
    const p: AdminPermitsParams = {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    };
    if (debouncedSearch) p.search = debouncedSearch;
    if (permitType !== "all") p.permit_type = permitType;
    if (status !== "all") p.status = status;
    if (sorting.length > 0 && sorting[0]) {
      p.sort = sorting[0].id;
      p.order = sorting[0].desc ? "desc" : "asc";
    }
    return p;
  }, [debouncedSearch, permitType, status, page, sorting]);

  const { data, isLoading } = useAdminPermits(params);

  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  function handleRowClick(permit: AdminPermitListItem) {
    void navigate({ to: "/permits/$permitId", params: { permitId: permit.id } });
  }

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  function handlePermitTypeChange(value: string) {
    setPermitType(value);
    setPage(1);
  }

  function handleStatusChange(value: string) {
    setStatus(value);
    setPage(1);
  }

  function handleReset() {
    setSearch("");
    setPermitType("all");
    setStatus("all");
    setPage(1);
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-slate-900">Permits</h1>

      <PermitFilters
        search={search}
        onSearchChange={handleSearchChange}
        permitType={permitType}
        onPermitTypeChange={handlePermitTypeChange}
        status={status}
        onStatusChange={handleStatusChange}
        onReset={handleReset}
      />

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        sorting={sorting}
        onSortingChange={setSorting}
        isLoading={isLoading}
        emptyMessage="No permits found"
        onRowClick={handleRowClick}
      />

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalItems={data?.total ?? 0}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  );
}
